import { Party, CreatePartyInput, PartyTransaction } from "@/types/party";
import { safeParseApiResponse } from "@/integrations/supabase/client";

function getHeaders(): Record<string, string> {
  const headers: Record<string, string> = { "Content-Type": "application/json" };
  try {
    const sessionStr = localStorage.getItem("accoupro_auth_session");
    const userStr = localStorage.getItem("accoupro_auth_user");
    if (sessionStr) {
      const sess = JSON.parse(sessionStr);
      if (sess?.access_token) headers["Authorization"] = `Bearer ${sess.access_token}`;
    }
    if (userStr) {
      const u = JSON.parse(userStr);
      if (u?.id) headers["x-user-id"] = u.id;
      if (u?.role) headers["x-user-role"] = u.role;
      if (u?.company || u?.company_id) headers["x-company-id"] = u.company || u.company_id;
    }
  } catch {}
  return headers;
}

export async function apiGetParties(params: {
  search?: string;
  type?: string;
  status?: string;
  state?: string;
  companyId?: string;
}): Promise<{ parties: Party[]; total: number }> {
  const query = new URLSearchParams();
  if (params.search) query.set("search", params.search);
  if (params.type) query.set("type", params.type);
  if (params.status) query.set("status", params.status);
  if (params.state) query.set("state", params.state);
  if (params.companyId) query.set("company_id", params.companyId);

  const res = await fetch(`/api/parties?${query.toString()}`, {
    headers: getHeaders(),
  });
  const parsed = await safeParseApiResponse<any>(res);
  if (!parsed.ok || parsed.error || !parsed.data?.success) {
    throw parsed.error || new Error(parsed.data?.error || "Failed to fetch parties");
  }
  return { parties: parsed.data.parties || [], total: parsed.data.total || 0 };
}

export async function apiGetPartyById(id: string): Promise<{
  party: Party;
  transactions: PartyTransaction[];
}> {
  const res = await fetch(`/api/parties/${id}`, {
    headers: getHeaders(),
  });
  const parsed = await safeParseApiResponse<any>(res);
  if (!parsed.ok || parsed.error || !parsed.data?.success) {
    throw parsed.error || new Error(parsed.data?.error || "Failed to fetch party");
  }
  return { party: parsed.data.party, transactions: parsed.data.transactions || [] };
}

export async function apiCreateParty(payload: CreatePartyInput & { companyId?: string }): Promise<{
  party: Party;
  selectedLedgerId: string;
}> {
  const res = await fetch("/api/parties", {
    method: "POST",
    headers: getHeaders(),
    body: JSON.stringify(payload),
  });
  const parsed = await safeParseApiResponse<any>(res);
  if (!parsed.ok || parsed.error || !parsed.data?.success) {
    throw parsed.error || new Error(parsed.data?.error || "Failed to create party");
  }
  return { party: parsed.data.party, selectedLedgerId: parsed.data.selectedLedgerId };
}

export async function apiUpdateParty(id: string, payload: Partial<CreatePartyInput>): Promise<{
  party: Party;
}> {
  const res = await fetch(`/api/parties/${id}`, {
    method: "PUT",
    headers: getHeaders(),
    body: JSON.stringify(payload),
  });
  const parsed = await safeParseApiResponse<any>(res);
  if (!parsed.ok || parsed.error || !parsed.data?.success) {
    throw parsed.error || new Error(parsed.data?.error || "Failed to update party");
  }
  return { party: parsed.data.party };
}

export async function apiArchiveParty(id: string): Promise<void> {
  const res = await fetch(`/api/parties/${id}/archive`, {
    method: "POST",
    headers: getHeaders(),
  });
  const parsed = await safeParseApiResponse<any>(res);
  if (!parsed.ok || parsed.error || !parsed.data?.success) {
    throw parsed.error || new Error(parsed.data?.error || "Failed to archive party");
  }
}

export async function apiRestoreParty(id: string): Promise<void> {
  const res = await fetch(`/api/parties/${id}/restore`, {
    method: "POST",
    headers: getHeaders(),
  });
  const parsed = await safeParseApiResponse<any>(res);
  if (!parsed.ok || parsed.error || !parsed.data?.success) {
    throw parsed.error || new Error(parsed.data?.error || "Failed to restore party");
  }
}
